import React, { useEffect, useMemo, useState } from 'react';
import {
  getApprovalBundle,
  approveWithEditedPrescription,
} from '../../services/api/patientPlanApi';
import { formatDoseSchedule, parseDoseSchedule } from '../../utils/rxDosage';

/**
 * ApprovalReviewDialog — the senior's in-depth review for a pending_approval
 * sitting. Opens via the SittingCard's "Review…" action; the default
 * "Approve" button on the card is the fast-approve path and skips this.
 *
 * Shows:
 *   - sitting summary (name, category, assignee, estimate)
 *   - PROCEDURE_NOTE entries written by the junior
 *   - PRESCRIPTION entries linked to the sitting, with a mini-preview
 *   - Approve (green) / Reject-with-reason (red) / Close buttons
 *
 * Props:
 *   planId, sittingId — which sitting is under review
 *   onApprove() — fires the approve mutation
 *   onReject({ rejection_reason }) — fires the reject mutation
 *   onClose() — dismiss without acting
 */
export default function ApprovalReviewDialog({
  planId,
  sittingId,
  onApprove,
  onReject,
  onClose,
  onEditedApproval, // Phase 1f — optional callback after successful edit+approve
}) {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Phase 1f — modes: 'preview' | 'rejecting' | 'editing'
  const [mode, setMode] = useState('preview');
  const [rejectionReason, setRejectionReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Phase 1f — local Rx-edit state for "Edit & approve" path.
  const [editDraftMetaId, setEditDraftMetaId] = useState(null);
  const [editMedicines, setEditMedicines] = useState([]);
  const [editDiagnosis, setEditDiagnosis] = useState('');
  const [editDoctorNotes, setEditDoctorNotes] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await getApprovalBundle(planId, sittingId);
      if (cancelled) return;
      if (r.success) {
        setBundle(r.data);
      } else {
        setError(r.error || 'Could not load review bundle.');
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [planId, sittingId]);

  const handleApprove = async () => {
    setBusy(true);
    try {
      await onApprove?.();
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    const trimmed = rejectionReason.trim();
    if (trimmed.length < 3) return;
    setBusy(true);
    try {
      await onReject?.({ rejection_reason: trimmed });
    } finally {
      setBusy(false);
    }
  };

  const s = bundle?.sitting;
  const notes = bundle?.procedure_notes || [];
  const prescriptions = useMemo(() => bundle?.prescriptions || [], [bundle?.prescriptions]);

  // Phase 1f — the draft the senior will edit. Prefer the most recent
  // pending_approval entry; fall back to whatever's there.
  const editableDraft = useMemo(() => {
    if (!prescriptions.length) return null;
    const pending = prescriptions.find(
      (p) => p.approval_status === 'pending_approval',
    );
    return pending || prescriptions[prescriptions.length - 1];
  }, [prescriptions]);

  const handleStartEdit = () => {
    if (!editableDraft) return;
    setEditDraftMetaId(editableDraft.meta_id);
    setEditMedicines(
      (editableDraft.medicines || []).map((m) => ({
        medicine: m.medicine || m.medicine_input || '',
        strength: m.strength || '',
        form: m.form || '',
        dosage_text: m.dosage_schedule
          ? formatDoseSchedule(m.dosage_schedule)
          : (m.dosage_text || ''),
        timing: m.timing || '',
        duration: m.duration || '',
        notes: m.notes || '',
      })),
    );
    setEditDiagnosis(editableDraft.diagnosis || '');
    setEditDoctorNotes(editableDraft.doctor_notes || '');
    setMode('editing');
  };

  const updateMedicine = (idx, field, value) => {
    setEditMedicines((prev) => prev.map((m, i) => (i === idx ? { ...m, [field]: value } : m)));
  };
  const removeMedicine = (idx) => {
    setEditMedicines((prev) => prev.filter((_, i) => i !== idx));
  };
  const addMedicine = () => {
    setEditMedicines((prev) => [
      ...prev,
      { medicine: '', strength: '', form: '', dosage_text: '', timing: '', duration: '', notes: '' },
    ]);
  };

  const handleSaveEdit = async () => {
    if (!editDraftMetaId) return;
    // Parse "M-A-N" dosage_text back into schedule (½ tablets included —
    // RX-HALF-DOSE: a bare Number() on a glyph slot used to silently zero
    // it; parseDoseSchedule is the shared, round-trip-safe parser).
    const payloadMedicines = editMedicines
      .filter((m) => (m.medicine || '').trim().length > 0)
      .map((m) => {
        const schedule = parseDoseSchedule(m.dosage_text) || { morning: 0, afternoon: 0, night: 0 };
        return {
          medicine: m.medicine.trim(),
          strength: m.strength || undefined,
          form: m.form || undefined,
          dosage_schedule: schedule,
          timing: m.timing || undefined,
          duration: m.duration || undefined,
          notes: m.notes || undefined,
          is_resolved: true,
        };
      });
    if (payloadMedicines.length === 0) {
      setError('Add at least one medicine before approving.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await approveWithEditedPrescription(planId, sittingId, {
        draft_meta_id: editDraftMetaId,
        medicines: payloadMedicines,
        diagnosis: editDiagnosis || undefined,
        doctor_notes: editDoctorNotes || undefined,
      });
      if (!r?.success) {
        setError(r?.error || 'Could not save edited prescription.');
        return;
      }
      if (onEditedApproval) onEditedApproval(r.data);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold">Review sitting</h2>
            {s && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {s.procedure_name || 'Procedure'}
                {s.procedure_category ? ` · ${s.procedure_category}` : ''}
                {' · ₹'}{s.final_cost ?? 0}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-lg leading-none px-1"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {loading && (
          <div className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">
            Loading…
          </div>
        )}

        {error && (
          <div className="mb-3 text-sm text-red-800 bg-red-50 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800 rounded p-2">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-4">
            {/* Assignee context (Phase 1c — prefer denorm name) */}
            {s && (
              <div className="text-xs text-slate-500 dark:text-slate-400">
                Assignee:{' '}
                <span className="font-semibold">
                  {s.assigned_to_role || '—'}
                </span>
                {' · '}
                <span>
                  {s.assigned_to_name || s.assigned_to_member_id || '—'}
                </span>
              </div>
            )}

            {/* Procedure notes */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Procedure notes ({notes.length})
              </h3>
              {notes.length === 0 ? (
                <div className="text-sm italic text-slate-400 py-1">
                  No procedure notes from the assignee.
                </div>
              ) : (
                <div className="space-y-2">
                  {notes.map((n) => (
                    <div
                      key={n.meta_id}
                      className="text-sm bg-slate-50 dark:bg-slate-800/50 rounded-md px-3 py-2"
                    >
                      <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                        {n.added_by_name}{n.added_by_role ? ` · ${n.added_by_role}` : ''}
                        {' · '}{n.created_at?.slice(0, 16).replace('T', ' ')}
                      </div>
                      <div className="whitespace-pre-wrap">{n.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Linked prescriptions (draft/pending) */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Draft prescription ({prescriptions.length})
              </h3>
              {prescriptions.length === 0 ? (
                <div className="text-sm italic text-slate-400 py-1">
                  No sub-prescription drafted.
                </div>
              ) : (
                <div className="space-y-2">
                  {prescriptions.map((p) => (
                    <div
                      key={p.meta_id}
                      className="text-sm bg-slate-50 dark:bg-slate-800/50 rounded-md px-3 py-2"
                    >
                      <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                        {p.added_by_name}{p.added_by_role ? ` · ${p.added_by_role}` : ''}
                        {p.approval_status
                          ? ` · ${p.approval_status.replace('_', ' ')}`
                          : ''}
                      </div>
                      {(p.medicines || []).map((m, i) => (
                        <div key={i} className="text-xs">
                          <span className="font-semibold">{m.medicine}</span>
                          {m.strength ? ` · ${m.strength}` : ''}
                          {m.form ? ` (${m.form})` : ''}
                          {m.dosage_schedule
                            ? ` · ${formatDoseSchedule(m.dosage_schedule)}`
                            : ''}
                          {m.timing ? ` · ${m.timing}` : ''}
                          {m.duration ? ` · ${m.duration}` : ''}
                        </div>
                      ))}
                      {p.doctor_notes && (
                        <div className="text-xs italic text-slate-500 mt-1">
                          {p.doctor_notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Reject input (conditional) */}
            {mode === 'rejecting' && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Reject with reason (min 3 chars) *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  placeholder="e.g. Change Paracetamol to Ibuprofen 400 — patient has liver issue."
                  minLength={3}
                  required
                />
              </div>
            )}

            {/* Phase 1f — inline Rx editor (Edit & approve path) */}
            {mode === 'editing' && (
              <div className="space-y-3 border-t border-slate-200 dark:border-slate-700 pt-3">
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Edit the medicines below; save to approve. The original draft
                  is preserved in the audit trail.
                </div>
                <div className="space-y-2">
                  {editMedicines.map((m, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-12 gap-2 items-start border border-slate-200 dark:border-slate-700 rounded-md p-2"
                    >
                      <input
                        type="text"
                        value={m.medicine}
                        onChange={(e) => updateMedicine(idx, 'medicine', e.target.value)}
                        placeholder="Medicine *"
                        className="col-span-4 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        value={m.strength}
                        onChange={(e) => updateMedicine(idx, 'strength', e.target.value)}
                        placeholder="Strength"
                        className="col-span-2 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        value={m.dosage_text}
                        onChange={(e) => updateMedicine(idx, 'dosage_text', e.target.value)}
                        placeholder="1-0-1"
                        className="col-span-2 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        value={m.duration}
                        onChange={(e) => updateMedicine(idx, 'duration', e.target.value)}
                        placeholder="5 days"
                        className="col-span-2 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      />
                      <button
                        type="button"
                        onClick={() => removeMedicine(idx)}
                        aria-label="Remove medicine"
                        className="col-span-1 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                      >
                        ✕
                      </button>
                      <input
                        type="text"
                        value={m.timing}
                        onChange={(e) => updateMedicine(idx, 'timing', e.target.value)}
                        placeholder="After food"
                        className="col-span-5 px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        value={m.notes}
                        onChange={(e) => updateMedicine(idx, 'notes', e.target.value)}
                        placeholder="Notes (optional)"
                        className="col-span-7 px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addMedicine}
                  className="text-xs text-teal-700 dark:text-teal-300 hover:underline"
                >
                  + Add medicine
                </button>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                    Diagnosis
                  </label>
                  <input
                    type="text"
                    value={editDiagnosis}
                    onChange={(e) => setEditDiagnosis(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                    Doctor notes
                  </label>
                  <textarea
                    value={editDoctorNotes}
                    onChange={(e) => setEditDoctorNotes(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end flex-wrap gap-2 mt-5 border-t border-slate-200 dark:border-slate-700 pt-4">
          {mode === 'preview' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => setMode('rejecting')}
                disabled={busy || loading || !!error}
                className="px-4 py-1.5 text-sm rounded-md border border-red-300 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                Reject…
              </button>
              {/* Phase 1f — Edit & approve only when there's an editable draft */}
              {editableDraft && (
                <button
                  type="button"
                  onClick={handleStartEdit}
                  disabled={busy || loading || !!error}
                  className="px-4 py-1.5 text-sm rounded-md border border-teal-400 dark:border-teal-500 text-teal-800 dark:text-teal-200 hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:opacity-50"
                >
                  Edit & approve
                </button>
              )}
              <button
                type="button"
                onClick={handleApprove}
                disabled={busy || loading || !!error}
                className="px-4 py-1.5 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-emerald-300"
              >
                {busy ? 'Approving…' : 'Approve as-is'}
              </button>
            </>
          )}
          {mode === 'rejecting' && (
            <>
              <button
                type="button"
                onClick={() => { setMode('preview'); setRejectionReason(''); }}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={busy || rejectionReason.trim().length < 3}
                className="px-4 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                {busy ? 'Rejecting…' : 'Confirm reject'}
              </button>
            </>
          )}
          {mode === 'editing' && (
            <>
              <button
                type="button"
                onClick={() => { setMode('preview'); setError(''); }}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={busy}
                className="px-4 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:bg-teal-300"
              >
                {busy ? 'Saving…' : 'Save & approve'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
